import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, addDoc, query, where, orderBy, updateDoc, doc, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { User } from 'firebase/auth';
import PageHeader from './ui/PageHeader';
import SectionCard from './ui/SectionCard';
import TabNav from './ui/TabNav';
import { containerClass } from '../config/theme';
import { partnerConnectService, PartnershipProfile, PartnershipMatch, PartnershipKit, LeadGenOpportunity, PartnerNote } from '../services/partnerConnectService';

interface PartnerConnectProps {
  userProfile: any;
}

const PartnerConnect: React.FC<PartnerConnectProps> = ({ userProfile }) => {
  const [activeTab, setActiveTab] = useState<'matches' | 'profile' | 'kit'>('matches');
  const [partnershipProfile, setPartnershipProfile] = useState<PartnershipProfile | null>(null);
  const [matches, setMatches] = useState<PartnershipMatch[]>([]);
  const [partnershipKit, setPartnershipKit] = useState<PartnershipKit | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<PartnershipMatch | null>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showKitModal, setShowKitModal] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    attractivePartnerReason: '',
    hiddenValue: '',
    partnershipGoals: [] as Array<{ type: 'leads' | 'events' | 'knowledge' | 'crosspromo' | 'product_bundles'; description: string; priority: 'high' | 'medium' | 'low' }>,
    addedValue: [] as string[],
    isOpenForPartnerships: false,
    targetAudience: [] as string[],
    brandPositioning: ''
  });

  // Kit form state
  const [kitForm, setKitForm] = useState({
    newOpportunity: {
      type: 'co_hosting' as LeadGenOpportunity['type'],
      title: '',
      description: '',
      potentialReach: 0,
      estimatedValue: 0
    },
    newNote: {
      partnerId: '',
      partnerName: '',
      strengths: [] as string[],
      overlap: [] as string[],
      rating: 5,
      notes: '',
      tags: [] as string[]
    }
  });

  useEffect(() => {
    if (auth.currentUser) {
      loadUserData();
    }
  }, [auth.currentUser]);

  const loadUserData = async () => {
    if (!auth.currentUser) return;
    
    setLoading(true);
    try {
      const [profile, userMatches, kit] = await Promise.all([
        partnerConnectService.getPartnershipProfile(auth.currentUser.uid),
        partnerConnectService.getUserPartnershipMatches(auth.currentUser.uid),
        partnerConnectService.getPartnershipKit(auth.currentUser.uid)
      ]);

      setPartnershipProfile(profile);
      setMatches(userMatches);
      setPartnershipKit(kit);

      if (profile) {
        setProfileForm({
          attractivePartnerReason: profile.attractivePartnerReason || '',
          hiddenValue: profile.hiddenValue || '',
          partnershipGoals: profile.partnershipGoals || [],
          addedValue: profile.addedValue || [],
          isOpenForPartnerships: profile.isOpenForPartnerships || false,
          targetAudience: profile.targetAudience || [],
          brandPositioning: profile.brandPositioning || ''
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const findNewMatches = async () => {
    if (!auth.currentUser) return;
    
    setLoading(true);
    try {
      const newMatches = await partnerConnectService.findPartnershipMatches(auth.currentUser.uid, 5);
      setMatches(prev => [...newMatches, ...prev]);
    } catch (error) {
      console.error('Error finding matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async () => {
    if (!auth.currentUser) return;
    
    setLoading(true);
    try {
      await partnerConnectService.updatePartnershipProfile(auth.currentUser.uid, profileForm);
      await loadUserData();
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPartnershipGoal = () => {
    setProfileForm(prev => ({
      ...prev,
      partnershipGoals: [...prev.partnershipGoals, {
        type: 'leads',
        description: '',
        priority: 'medium'
      }]
    }));
  };

  const updatePartnershipGoal = (index: number, field: string, value: any) => {
    setProfileForm(prev => ({
      ...prev,
      partnershipGoals: prev.partnershipGoals.map((goal, i) => 
        i === index ? { ...goal, [field]: value } : goal
      )
    }));
  };

  const removePartnershipGoal = (index: number) => {
    setProfileForm(prev => ({
      ...prev,
      partnershipGoals: prev.partnershipGoals.filter((_, i) => i !== index)
    }));
  };

  const addValueField = () => {
    setProfileForm(prev => ({
      ...prev,
      addedValue: [...prev.addedValue, '']
    }));
  };

  const updateValueField = (index: number, value: string) => {
    setProfileForm(prev => ({
      ...prev,
      addedValue: prev.addedValue.map((v, i) => i === index ? value : v)
    }));
  };

  const removeValueField = (index: number) => {
    setProfileForm(prev => ({
      ...prev,
      addedValue: prev.addedValue.filter((_, i) => i !== index)
    }));
  };

  const addAudienceField = () => {
    setProfileForm(prev => ({
      ...prev,
      targetAudience: [...prev.targetAudience, '']
    }));
  };

  const updateAudienceField = (index: number, value: string) => {
    setProfileForm(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.map((a, i) => i === index ? value : a)
    }));
  };

  const removeAudienceField = (index: number) => {
    setProfileForm(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter((_, i) => i !== index)
    }));
  };

  const updateMatchStatus = async (matchId: string, status: PartnershipMatch['status']) => {
    try {
      await partnerConnectService.updatePartnershipMatchStatus(matchId, status);
      setMatches(prev => prev.map(match => 
        match.id === matchId ? { ...match, status } : match
      ));
    } catch (error) {
      console.error('Error updating match status:', error);
    }
  };

  const getStatusColor = (status: PartnershipMatch['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'meeting_scheduled': return 'bg-purple-100 text-purple-800';
      case 'deal_in_progress': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: PartnershipMatch['status']) => {
    switch (status) {
      case 'pending': return 'In afwachting';
      case 'contacted': return 'Gecontacteerd';
      case 'meeting_scheduled': return 'Meeting gepland';
      case 'deal_in_progress': return 'Deal in uitvoering';
      case 'completed': return 'Voltooid';
      case 'declined': return 'Afgewezen';
      default: return status;
    }
  };

  const getTimelineText = (timeline: string) => {
    switch (timeline) {
      case 'immediate': return 'Direct';
      case 'short_term': return 'Korte termijn';
      case 'long_term': return 'Lange termijn';
      default: return timeline;
    }
  };

  const getTimelineColor = (timeline: string) => {
    switch (timeline) {
      case 'immediate': return 'bg-red-100 text-red-800';
      case 'short_term': return 'bg-yellow-100 text-yellow-800';
      case 'long_term': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <PageHeader 
        title="PartnerConnect"
        subtitle="Strategische samenwerkingen vinden en beheren met AI-powered matching"
        icon={<span>🤝</span>}
      />
      
      {/* Tab Navigation */}
      <TabNav 
        tabs={[
          { href: '#matches', label: 'Matches', icon: <span>🎯</span>, onClick: () => setActiveTab('matches') },
          { href: '#profile', label: 'Profiel', icon: <span>👤</span>, onClick: () => setActiveTab('profile') },
          { href: '#kit', label: 'Partnership Kit', icon: <span>🧰</span>, onClick: () => setActiveTab('kit') },
        ]}
        activeTab={activeTab}
      />
      
      {/* Main Content */}
      <div className={containerClass}>
        <div className="grid gap-6">
          {/* Matches Tab */}
          {activeTab === 'matches' && (
            <SectionCard>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Jouw Partnership Matches</h2>
                <button
                  onClick={findNewMatches}
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Zoeken...' : 'Nieuwe Matches Vinden'}
                </button>
              </div>

              {matches.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🤝</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Nog geen matches</h3>
                  <p className="text-gray-600 mb-4">
                    Vul eerst je partnership profiel in en vind strategische samenwerkingen
                  </p>
                  <button
                    onClick={() => setActiveTab('profile')}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                  >
                    Profiel Invullen
                  </button>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {matches.map((match) => (
                    <div key={match.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                            {match.matchScore}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Match Score</h3>
                            <p className="text-sm text-gray-600">Partner #{match.partnerId.slice(-6)}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(match.status)}`}>
                          {getStatusText(match.status)}
                        </span>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Match Redenen:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {match.matchReasons.map((reason, index) => (
                              <li key={index} className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Partnership Potential:</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Doelgroep:</span>
                              <span className="ml-1 font-medium">{Math.round(match.partnershipPotential.audienceOverlap)}%</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Doelen:</span>
                              <span className="ml-1 font-medium">{Math.round(match.partnershipPotential.goalAlignment)}%</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Waarde:</span>
                              <span className="ml-1 font-medium">{Math.round(match.partnershipPotential.valueComplementarity)}%</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Merk:</span>
                              <span className="ml-1 font-medium">{Math.round(match.partnershipPotential.brandCompatibility)}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            Geschatte waarde: €{match.partnershipPotential.estimatedDealValue}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTimelineColor(match.partnershipPotential.suggestedTimeline)}`}>
                            {getTimelineText(match.partnershipPotential.suggestedTimeline)}
                          </span>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedMatch(match);
                            setShowMatchModal(true);
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                        >
                          Details
                        </button>
                        {match.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateMatchStatus(match.id, 'contacted')}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                            >
                              Contact
                            </button>
                            <button
                              onClick={() => updateMatchStatus(match.id, 'declined')}
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                            >
                              Afwijzen
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <SectionCard>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Partnership Profiel</h2>
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                >
                  {partnershipProfile ? 'Profiel Bewerken' : 'Profiel Aanmaken'}
                </button>
              </div>

              {partnershipProfile ? (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Aantrekkelijke Partner Omdat:</h3>
                      <p className="text-gray-600">{partnershipProfile.attractivePartnerReason}</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Verborgen Waarde:</h3>
                      <p className="text-gray-600">{partnershipProfile.hiddenValue}</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Partnership Doelen:</h3>
                      <div className="space-y-2">
                        {partnershipProfile.partnershipGoals.map((goal, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              goal.priority === 'high' ? 'bg-red-100 text-red-800' :
                              goal.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {goal.type}
                            </span>
                            <span className="text-gray-600">{goal.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Toegevoegde Waarde:</h3>
                      <div className="flex flex-wrap gap-2">
                        {partnershipProfile.addedValue.map((value, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                            {value}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Doelgroep:</h3>
                      <div className="flex flex-wrap gap-2">
                        {partnershipProfile.targetAudience.map((audience, index) => (
                          <span key={index} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm">
                            {audience}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Merkpositionering:</h3>
                      <p className="text-gray-600">{partnershipProfile.brandPositioning}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👤</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Nog geen partnership profiel</h3>
                  <p className="text-gray-600 mb-4">
                    Maak je partnership profiel aan om matches te vinden
                  </p>
                  <button
                    onClick={() => setShowProfileModal(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                  >
                    Profiel Aanmaken
                  </button>
                </div>
              )}
            </SectionCard>
          )}

          {/* Kit Tab */}
          {activeTab === 'kit' && (
            <SectionCard>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Partnership Kit</h2>
                <button
                  onClick={() => setShowKitModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                >
                  Kit Beheren
                </button>
              </div>

              {partnershipKit ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Lead Generation Opportunities</h3>
                    <div className="space-y-3">
                      {partnershipKit.leadGenerationOpportunities.map((opportunity) => (
                        <div key={opportunity.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{opportunity.title}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              opportunity.isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {opportunity.isCompleted ? 'Voltooid' : 'Open'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{opportunity.description}</p>
                          <div className="flex justify-between text-sm text-gray-500">
                            <span>Bereik: {opportunity.potentialReach}</span>
                            <span>Waarde: €{opportunity.estimatedValue}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Partner Notities</h3>
                    <div className="space-y-3">
                      {partnershipKit.partnerNotes.map((note) => (
                        <div key={note.partnerId} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{note.partnerName}</h4>
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={star} className={`text-sm ${star <= note.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                                  ★
                                </span>
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{note.notes}</p>
                          <div className="flex flex-wrap gap-1">
                            {note.tags.map((tag, index) => (
                              <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🧰</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Nog geen partnership kit</h3>
                  <p className="text-gray-600 mb-4">
                    Start met het opbouwen van je partnership toolkit
                  </p>
                  <button
                    onClick={() => setShowKitModal(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                  >
                    Kit Aanmaken
                  </button>
                </div>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      {/* Match Details Modal */}
      {showMatchModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Match Details</h2>
                <button
                  onClick={() => setShowMatchModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Voorgestelde Kansen:</h3>
                  <div className="space-y-3">
                    {selectedMatch.suggestedOpportunities.map((opportunity) => (
                      <div key={opportunity.id} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">{opportunity.title}</h4>
                        <p className="text-gray-600 mb-3">{opportunity.description}</p>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Potentieel bereik: {opportunity.potentialReach}</span>
                          <span>Geschatte waarde: €{opportunity.estimatedValue}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      updateMatchStatus(selectedMatch.id, 'contacted');
                      setShowMatchModal(false);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors"
                  >
                    Contact Opnemen
                  </button>
                  <button
                    onClick={() => setShowMatchModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-medium transition-colors"
                  >
                    Sluiten
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Partnership Profiel</h2>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ik ben een aantrekkelijke partner omdat...
                  </label>
                  <textarea
                    value={profileForm.attractivePartnerReason}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, attractivePartnerReason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Beschrijf waarom andere ondernemers met jou zouden willen samenwerken..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mijn verborgen waarde als samenwerkingspartner is...
                  </label>
                  <textarea
                    value={profileForm.hiddenValue}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, hiddenValue: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Wat bied je dat niet direct zichtbaar is in je aanbod?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partnership Doelen
                  </label>
                  <div className="space-y-3">
                    {profileForm.partnershipGoals.map((goal, index) => (
                      <div key={index} className="flex space-x-2">
                        <select
                          value={goal.type}
                          onChange={(e) => updatePartnershipGoal(index, 'type', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="leads">Leads genereren</option>
                          <option value="events">Events organiseren</option>
                          <option value="knowledge">Kennis bundelen</option>
                          <option value="crosspromo">Cross-promotie</option>
                          <option value="product_bundles">Product bundels</option>
                        </select>
                        <input
                          type="text"
                          value={goal.description}
                          onChange={(e) => updatePartnershipGoal(index, 'description', e.target.value)}
                          placeholder="Beschrijving"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                          value={goal.priority}
                          onChange={(e) => updatePartnershipGoal(index, 'priority', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="low">Laag</option>
                          <option value="medium">Medium</option>
                          <option value="high">Hoog</option>
                        </select>
                        <button
                          onClick={() => removePartnershipGoal(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addPartnershipGoal}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Doel toevoegen
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ik bied toegevoegde waarde op het gebied van...
                  </label>
                  <div className="space-y-2">
                    {profileForm.addedValue.map((value, index) => (
                      <div key={index} className="flex space-x-2">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateValueField(index, e.target.value)}
                          placeholder="Bijv. marketing, sales, design, development..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => removeValueField(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addValueField}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Waarde toevoegen
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doelgroep
                  </label>
                  <div className="space-y-2">
                    {profileForm.targetAudience.map((audience, index) => (
                      <div key={index} className="flex space-x-2">
                        <input
                          type="text"
                          value={audience}
                          onChange={(e) => updateAudienceField(index, e.target.value)}
                          placeholder="Bijv. startups, scale-ups, freelancers..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => removeAudienceField(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addAudienceField}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Doelgroep toevoegen
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Merkpositionering
                  </label>
                  <input
                    type="text"
                    value={profileForm.brandPositioning}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, brandPositioning: e.target.value }))}
                    placeholder="Bijv. premium, affordable, innovative, traditional..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="openForPartnerships"
                    checked={profileForm.isOpenForPartnerships}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, isOpenForPartnerships: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="openForPartnerships" className="ml-2 block text-sm text-gray-900">
                    Open voor nieuwe partnerships
                  </label>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={updateProfile}
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Opslaan...' : 'Profiel Opslaan'}
                  </button>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-medium transition-colors"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kit Modal */}
      {showKitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Partnership Kit Beheren</h2>
                <button
                  onClick={() => setShowKitModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Lead Generation Opportunities</h3>
                  <div className="space-y-3">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Nieuwe Kans Toevoegen</h4>
                      <div className="space-y-3">
                        <select
                          value={kitForm.newOpportunity.type}
                          onChange={(e) => setKitForm(prev => ({
                            ...prev,
                            newOpportunity: { ...prev.newOpportunity, type: e.target.value as any }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="co_hosting">Co-hosting</option>
                          <option value="masterclass">Masterclass</option>
                          <option value="challenge">Challenge</option>
                          <option value="giveaway">Giveaway</option>
                          <option value="webinar">Webinar</option>
                          <option value="podcast">Podcast</option>
                          <option value="workshop">Workshop</option>
                          <option value="event">Event</option>
                          <option value="product_launch">Product Launch</option>
                          <option value="content_collaboration">Content Collaboration</option>
                        </select>
                        <input
                          type="text"
                          value={kitForm.newOpportunity.title}
                          onChange={(e) => setKitForm(prev => ({
                            ...prev,
                            newOpportunity: { ...prev.newOpportunity, title: e.target.value }
                          }))}
                          placeholder="Titel"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <textarea
                          value={kitForm.newOpportunity.description}
                          onChange={(e) => setKitForm(prev => ({
                            ...prev,
                            newOpportunity: { ...prev.newOpportunity, description: e.target.value }
                          }))}
                          placeholder="Beschrijving"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            value={kitForm.newOpportunity.potentialReach}
                            onChange={(e) => setKitForm(prev => ({
                              ...prev,
                              newOpportunity: { ...prev.newOpportunity, potentialReach: parseInt(e.target.value) }
                            }))}
                            placeholder="Potentieel bereik"
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input
                            type="number"
                            value={kitForm.newOpportunity.estimatedValue}
                            onChange={(e) => setKitForm(prev => ({
                              ...prev,
                              newOpportunity: { ...prev.newOpportunity, estimatedValue: parseInt(e.target.value) }
                            }))}
                            placeholder="Geschatte waarde"
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors">
                          Kans Toevoegen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Partner Notitie</h3>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={kitForm.newNote.partnerName}
                        onChange={(e) => setKitForm(prev => ({
                          ...prev,
                          newNote: { ...prev.newNote, partnerName: e.target.value }
                        }))}
                        placeholder="Partner naam"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <textarea
                        value={kitForm.newNote.notes}
                        onChange={(e) => setKitForm(prev => ({
                          ...prev,
                          newNote: { ...prev.newNote, notes: e.target.value }
                        }))}
                        placeholder="Notities over deze partner..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={4}
                      />
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Rating:</span>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setKitForm(prev => ({
                              ...prev,
                              newNote: { ...prev.newNote, rating: star }
                            }))}
                            className={`text-lg ${star <= kitForm.newNote.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={kitForm.newNote.tags.join(', ')}
                        onChange={(e) => setKitForm(prev => ({
                          ...prev,
                          newNote: { ...prev.newNote, tags: e.target.value.split(',').map(tag => tag.trim()) }
                        }))}
                        placeholder="Tags (komma-gescheiden)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors">
                        Notitie Toevoegen
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowKitModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-medium transition-colors"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerConnect;
